import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, useLocalizeMessage } from 'libs.nucleus';
import { FilterBar } from 'libs.react';
import { FilterSelect, FilterBarControlWidth } from './filter_select.component';
import { getOperatorLabel } from '../../utils';
import { MAX_BADGE_VALUES_SHOWN, NO_VALUE_SENTINEL, OPERATOR_BADGE_SHORTHAND, buildFilterParams, filterStateKey, isNoValueOperator, isDateRangeFromUnset, hasFilterValues, } from '../../utils/dashboard_filters.utils';
export const DashboardFilters = ({ filters, studyId, environmentId, runCount, cubeConfigIds = [], cubeApi, analyticsEnv, onApply, layout = 'dialog', }) => {
    const [appliedFilters, setAppliedFilters] = useState({});
    const [pendingFilters, setPendingFilters] = useState({});
    const [appliedOperators, setAppliedOperators] = useState({});
    const [pendingOperators, setPendingOperators] = useState({});
    const [filterFormGeneration, setFilterFormGeneration] = useState(0);
    const prevDefaultsKeyRef = useRef('');
    const sortedFilters = useMemo(() => [...filters].sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity)), [filters]);
    const filterByStateKey = useMemo(() => {
        const map = {};
        for (const f of filters) {
            map[filterStateKey(f)] = f;
        }
        return map;
    }, [filters]);
    const operatorByStateKey = useMemo(() => {
        const map = {};
        for (const f of filters) {
            map[filterStateKey(f)] = f.defaultOperator || f.operator || 'equals';
        }
        return map;
    }, [filters]);
    const roleByStateKey = useMemo(() => {
        const map = {};
        for (const f of filters) {
            map[filterStateKey(f)] = f.role;
        }
        return map;
    }, [filters]);
    const defaultsKey = useMemo(() => JSON.stringify(filters.map((f) => ({ k: filterStateKey(f), dv: f.defaultValues }))), [filters]);
    useEffect(() => {
        if (defaultsKey === prevDefaultsKeyRef.current) {
            return;
        }
        prevDefaultsKeyRef.current = defaultsKey;
        const defaults = {};
        const operators = { ...operatorByStateKey };
        for (const f of filters) {
            if (f.defaultValues?.length) {
                defaults[filterStateKey(f)] = f.defaultValues.map((v) => ({ label: v, value: v }));
            }
        }
        setAppliedFilters(defaults);
        setPendingFilters(defaults);
        setAppliedOperators(operators);
        setPendingOperators(operators);
    }, [defaultsKey, filters, operatorByStateKey]);
    const effectiveAppliedOperators = useMemo(() => ({ ...operatorByStateKey, ...appliedOperators }), [operatorByStateKey, appliedOperators]);
    const activeFilters = useMemo(() => {
        return Object.entries(appliedFilters)
            .filter(([stateKey, opts]) => hasFilterValues(effectiveAppliedOperators[stateKey] || 'equals', opts))
            .map(([stateKey, opts]) => {
            const filter = filterByStateKey[stateKey];
            const colonIdx = stateKey.indexOf(':');
            const memberKey = colonIdx >= 0 ? stateKey.slice(0, colonIdx) : stateKey;
            const filterLabel = filter?.label ?? memberKey;
            const operator = effectiveAppliedOperators[stateKey];
            const opShort = OPERATOR_BADGE_SHORTHAND[operator] ?? operator;
            // No-value operators: badge shows "fieldName: is set" / "fieldName: is not set".
            if (isNoValueOperator(operator)) {
                return { id: stateKey, label: `${filterLabel}: ${opShort}` };
            }
            const visibleOpts = opts.filter((o) => o.value !== NO_VALUE_SENTINEL && !isDateRangeFromUnset(o.value));
            const shown = visibleOpts
                .slice(0, MAX_BADGE_VALUES_SHOWN)
                .map((o) => o.label)
                .join(', ');
            const extra = visibleOpts.length - MAX_BADGE_VALUES_SHOWN;
            const valuesLabel = extra > 0 ? `${shown}, +${extra} more` : shown;
            // Badge format: "fieldName op values" e.g. "Site = DUMMY-001" / "Date in range 2024-01-01"
            return { id: stateKey, label: `${filterLabel} ${opShort} ${valuesLabel}` };
        });
    }, [appliedFilters, filterByStateKey, effectiveAppliedOperators]);
    const handleRemoveFilter = useCallback((badge) => {
        const next = { ...appliedFilters, [badge.id]: [] };
        setAppliedFilters(next);
        setPendingFilters(next);
        onApply(buildFilterParams(next, effectiveAppliedOperators, roleByStateKey));
    }, [appliedFilters, effectiveAppliedOperators, roleByStateKey, onApply]);
    const handleSubmit = useCallback(() => {
        const mergedOperators = { ...operatorByStateKey, ...pendingOperators };
        setAppliedFilters(pendingFilters);
        setAppliedOperators(mergedOperators);
        onApply(buildFilterParams(pendingFilters, mergedOperators, roleByStateKey));
    }, [pendingFilters, pendingOperators, operatorByStateKey, roleByStateKey, onApply]);
    const handleCancel = useCallback(() => {
        setPendingFilters(appliedFilters);
        setPendingOperators(appliedOperators);
        setFilterFormGeneration((generation) => generation + 1);
    }, [appliedFilters, appliedOperators]);
    const handleClearAll = useCallback(() => {
        setAppliedFilters({});
        setPendingFilters({});
        setAppliedOperators({});
        setPendingOperators({});
        setFilterFormGeneration((generation) => generation + 1);
        onApply([]);
    }, [onApply]);
    // Group same-key filters so they render side by side in one row.
    const groupedFilters = useMemo(() => {
        const groups = [];
        const keyToGroupIdx = new Map();
        for (const filter of sortedFilters) {
            if (keyToGroupIdx.has(filter.key)) {
                groups[keyToGroupIdx.get(filter.key)].push(filter);
            }
            else {
                keyToGroupIdx.set(filter.key, groups.length);
                groups.push([filter]);
            }
        }
        return groups;
    }, [sortedFilters]);
    return (_jsx("div", { className: 'mb-4', children: _jsx(FilterBar, { dataTestId: 'dashboard-filter-bar', layout: layout, modalWidth: 'base', activeFilters: activeFilters, onRemoveFilter: handleRemoveFilter, onSubmit: handleSubmit, onCancel: handleCancel, onClearAll: handleClearAll, children: _jsx("div", { className: 'flex w-full max-w-full min-w-0 flex-col gap-6', children: groupedFilters.map((group) => {
                    if (group.length === 1) {
                        const filter = group[0];
                        const stateKey = filterStateKey(filter);
                        const selectedOperator = pendingOperators[stateKey] || operatorByStateKey[stateKey] || 'equals';
                        return (_jsx("div", { className: 'w-full min-w-0', children: _jsx(FilterSelect, { filter: filter, studyId: studyId, environmentId: environmentId, runCount: runCount, cubeConfigIds: cubeConfigIds, cubeApi: cubeApi, analyticsEnv: analyticsEnv, selectedOptions: pendingFilters[stateKey] ?? [], onChange: (options) => setPendingFilters((prev) => ({ ...prev, [stateKey]: options })), selectedOperator: selectedOperator }) }, stateKey));
                    }
                    // Multiple filters for the same field — left: value picker, right: operator picker.
                    return (_jsx("div", { className: 'w-full min-w-0', children: _jsx(GroupedFilterRow, { group: group, studyId: studyId, environmentId: environmentId, runCount: runCount, cubeConfigIds: cubeConfigIds, cubeApi: cubeApi, analyticsEnv: analyticsEnv, pendingFilters: pendingFilters, onPendingChange: (stateKey, options) => setPendingFilters((prev) => ({ ...prev, [stateKey]: options })) }) }, `${group[0].key}-${filterFormGeneration}`));
                }) }) }) }));
};
const GroupedFilterRow = ({ group, studyId, environmentId, runCount, cubeConfigIds, cubeApi, analyticsEnv, pendingFilters, onPendingChange, }) => {
    const translate = useLocalizeMessage();
    const operatorOptions = group.map((f) => {
        const op = f.defaultOperator || f.operator || 'equals';
        // Measures compare aggregated values with numeric operators, even when the
        // underlying field is a time MIN/MAX.
        const opLabelKey = getOperatorLabel(f.role === 'measure' ? 'number' : f.fieldType, op);
        return { value: op, label: opLabelKey ? translate(opLabelKey) : op };
    });
    // Track the active operator. Initialise from whichever filter already has
    // pending values, falling back to the first in the group.
    const [activeOperator, setActiveOperator] = useState(() => {
        const withValues = group.find((f) => (pendingFilters[filterStateKey(f)] ?? []).length > 0);
        return ((withValues ? withValues.defaultOperator || withValues.operator : null) ?? operatorOptions[0]?.value ?? 'equals');
    });
    const activeFilter = group.find((f) => (f.defaultOperator || f.operator) === activeOperator) ?? group[0];
    const activeStateKey = filterStateKey(activeFilter);
    const selectedOperatorOption = operatorOptions.find((o) => o.value === activeOperator) ?? operatorOptions[0];
    const handleOperatorChange = useCallback((opt) => {
        if (opt.value === activeOperator) {
            return;
        }
        onPendingChange(activeStateKey, []);
        setActiveOperator(opt.value);
    }, [activeOperator, activeStateKey, onPendingChange]);
    return (_jsx(FilterSelect, { filter: activeFilter, studyId: studyId, environmentId: environmentId, runCount: runCount, cubeConfigIds: cubeConfigIds, cubeApi: cubeApi, analyticsEnv: analyticsEnv, selectedOptions: pendingFilters[activeStateKey] ?? [], onChange: (options) => onPendingChange(activeStateKey, options), selectedOperator: activeOperator, label: activeFilter.label, hideOperatorControl: true, selectWidth: 'full', suffix: _jsx(FilterBarControlWidth, { children: _jsx(Select, { label: translate('Operator'), options: operatorOptions, value: selectedOperatorOption, onChange: handleOperatorChange, width: 'full' }) }) }));
};
